import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Send, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { getApiBase, getWebBase } from "@/lib/env";
import { getDisplayNameFromToken } from "@/lib/jwt-claims";
import { useAuthStore } from "@/stores/auth-store";

type ChatTurn = {
  role: "user" | "assistant";
  content: string;
  localId?: string;
  turnId?: string;
  sourceTitles?: string[];
  lowConfidence?: boolean;
  guardrailBlocked?: boolean;
  feedbackSubmitted?: boolean;
};

const STORAGE_SESSION = "admin.bb-fah.session.v1";
const STORAGE_ANON = "admin.bb-fah.anon.v1";

const FETCH_MS = 90_000;
const MASCOT_SRC = "/rose-mascot-cutout.png";

const SUGGESTED_PROMPTS = [
  "What products does Xmegami offer?",
  "How do I join as a reseller?",
  "Shipping and delivery information",
] as const;

/** Appended to every assistant message (welcome + API + errors) for transparency */
const AI_DISCLAIMER =
  "\n\n— I'm only an AI assistant — please use this as a reference and confirm with official Xmegami channels when it matters.";
const AI_DISCLAIMER_ZH =
  "\n\n— 我只是 AI 助手，内容仅供参考；重要事项请以 Xmegami 官方渠道确认为准。";

function containsChinese(text: string): boolean {
  return /[\u3400-\u9fff]/.test(text);
}

function withDisclaimer(text: string): string {
  const t = text.trim();
  const disclaimer = containsChinese(t) ? AI_DISCLAIMER_ZH : AI_DISCLAIMER;
  if (!t) return disclaimer.trim();
  if (
    t.includes("only an AI assistant") ||
    t.includes("I'm only an AI") ||
    t.includes("我只是 AI 助手")
  )
    return t;
  return t + disclaimer;
}

function isSafeChatHref(href: string): boolean {
  const h = href.trim();
  if (!h) return false;
  if (h.includes("javascript:") || h.includes("data:")) return false;
  if (h.startsWith("/reseller/products/")) return true;
  return /^https?:\/\//i.test(h);
}

const CHAT_LINK_CLASS =
  "break-all font-medium text-[#b34778] underline underline-offset-2 hover:text-[#d54f8c]";
const CHAT_LINK_LIGHT_CLASS =
  "break-all font-medium text-white/95 underline underline-offset-2 hover:text-white";

function renderPlainWithAutoUrls(keyPrefix: string, s: string, lightLinks = false): ReactNode[] {
  const linkClass = lightLinks ? CHAT_LINK_LIGHT_CLASS : CHAT_LINK_CLASS;
  const URL = /https?:\/\/[^\s\])>]+/gi;
  const out: React.ReactNode[] = [];
  let i = 0;
  for (;;) {
    URL.lastIndex = i;
    const m = URL.exec(s);
    if (!m) {
      if (i < s.length) out.push(s.slice(i));
      break;
    }
    if (m.index > i) out.push(s.slice(i, m.index));
    const raw = m[0];
    const trailing = raw.match(/[.,;:!?]+$/)?.[0] ?? "";
    const href = trailing.length > 0 ? raw.slice(0, -trailing.length) : raw;
    if (isSafeChatHref(href)) {
      out.push(
        <a
          key={`${keyPrefix}-u-${m.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {href}
        </a>,
      );
      if (trailing) out.push(trailing);
    } else {
      out.push(raw);
    }
    i = m.index + raw.length;
  }
  return out;
}

function renderChatRichText(text: string, keyPrefix: string, lightLinks = false): ReactNode {
  const linkClass = lightLinks ? CHAT_LINK_LIGHT_CLASS : CHAT_LINK_CLASS;
  const MD = /\[([^\]]+)\]\(([^)]+)\)/g;
  const nodes: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let mdIdx = 0;
  while ((m = MD.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(...renderPlainWithAutoUrls(`${keyPrefix}-p${m.index}`, text.slice(last, m.index), lightLinks));
    }
    const label = m[1];
    const href = m[2].trim();
    if (isSafeChatHref(href)) {
      const resolvedHref = href.startsWith("/") ? `${getWebBase()}${href}` : href;
      nodes.push(
        <a
          key={`${keyPrefix}-md-${mdIdx++}`}
          href={resolvedHref}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {label}
        </a>,
      );
    } else {
      nodes.push(m[0]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    nodes.push(...renderPlainWithAutoUrls(`${keyPrefix}-tail`, text.slice(last), lightLinks));
  }
  return <>{nodes}</>;
}

function buildWelcome(): string {
  const token = useAuthStore.getState().token;
  const name = token ? getDisplayNameFromToken(token)?.trim() : "";
  if (name) {
    return withDisclaimer(
      `Hi ${name} — admin test mode for BB FAH. Ask me anything about Xmegami using our official company materials.`,
    );
  }
  return withDisclaimer(
    `Hello! Admin test mode for BB FAH — ask me anything about Xmegami using our official company materials.`,
  );
}

function getOrCreateAnonId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.localStorage.getItem(STORAGE_ANON);
    if (existing && existing.length >= 8) return existing;
    const id = crypto.randomUUID();
    window.localStorage.setItem(STORAGE_ANON, id);
    return id;
  } catch {
    return "";
  }
}

type SupportChatResponse = {
  message?: string;
  rateLimited?: boolean;
  missingApiKey?: boolean;
  sessionId?: string;
  lastTurnId?: string;
  sourceTitles?: string[];
  lowConfidence?: boolean;
  guardrailBlocked?: boolean;
  knowledgeBaseUpdatedUtc?: string;
  handoffUrl?: string | null;
};

type SseEventHandler = (eventName: string, data: unknown) => void;

async function readSseStream(res: Response, onEvent: SseEventHandler): Promise<void> {
  const reader = res.body?.getReader();
  if (!reader) throw new Error("Streaming is not available in this browser.");

  const decoder = new TextDecoder();
  let buf = "";

  function consumeBlock(block: string) {
    let eventName = "message";
    const dataLines: string[] = [];

    for (const rawLine of block.split("\n")) {
      const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
      if (line.startsWith("event:")) {
        eventName = line.slice("event:".length).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).trimStart());
      }
    }

    if (dataLines.length === 0) return;
    const rawData = dataLines.join("\n");
    try {
      onEvent(eventName, JSON.parse(rawData));
    } catch {
      onEvent(eventName, rawData);
    }
  }

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    for (;;) {
      const idx = buf.indexOf("\n\n");
      if (idx < 0) break;
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      consumeBlock(block);
    }
  }

  buf += decoder.decode();
  if (buf.trim()) consumeBlock(buf);
}

export function BbFahToggleButton({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      data-table-action
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
        open
          ? "bg-primary/12 text-foreground"
          : "text-muted hover:bg-hover-subtle hover:text-foreground"
      }`}
      aria-expanded={open}
      aria-controls="bb-fah-assistant-panel"
      aria-label={open ? "Close BB FAH Assistant" : "Open BB FAH Assistant"}
      title="BB FAH Assistant"
      onClick={onToggle}
    >
      <img
        src={MASCOT_SRC}
        alt=""
        width={28}
        height={36}
        className="h-8 w-6 object-contain object-bottom"
        draggable={false}
      />
    </button>
  );
}

export function BbFahAssistant({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const inputHistoryRef = useRef<string[]>([]);
  const inputHistoryIndexRef = useRef<number | null>(null);
  const inputHistoryDraftRef = useRef("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<ChatTurn[]>([{ role: "assistant", content: buildWelcome() }]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  const [knowledgeUpdatedLabel, setKnowledgeUpdatedLabel] = useState<string | null>(null);

  const setPanelOpen = useCallback(
    (next: boolean) => {
      onOpenChange(next);
    },
    [onOpenChange],
  );

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(STORAGE_SESSION);
    if (stored) {
      sessionIdRef.current = stored;
      const id = window.setTimeout(() => setSessionId(stored), 0);
      return () => window.clearTimeout(id);
    }
  }, []);

  useEffect(() => {
    const env = (import.meta.env.VITE_SUPPORT_HANDOFF_URL as string | undefined)?.trim();
    if (!env) return;
    const id = window.setTimeout(() => setHandoffUrl(env), 0);
    return () => window.clearTimeout(id);
  }, []);

  const openChat = useCallback(() => {
    setPanelOpen(true);
  }, [setPanelOpen]);

  useEffect(() => {
    const onProductPrompt = (event: Event) => {
      const detail = (event as CustomEvent<{ prompt?: unknown }>).detail;
      const prompt = typeof detail?.prompt === "string" ? detail.prompt.trim() : "";
      if (!prompt) return;
      inputHistoryIndexRef.current = null;
      inputHistoryDraftRef.current = "";
      setInput(prompt);
      openChat();
    };

    window.addEventListener("xmegami:support-chat:prompt", onProductPrompt);
    return () => window.removeEventListener("xmegami:support-chat:prompt", onProductPrompt);
  }, [openChat]);

  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      setMessages((prev) => {
        if (prev.length !== 1 || prev[0].role !== "assistant") return prev;
        return [{ role: "assistant", content: buildWelcome() }];
      });
      inputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPanelOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setPanelOpen]);

  const submitFeedback = useCallback(async (turnId: string, helpful: boolean) => {
    const sid = sessionIdRef.current;
    if (!sid || !turnId) return;
    try {
      const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      const res = await fetch(`${getApiBase()}/web/ai/support-chat/feedback`, {
        method: "POST",
        headers,
        body: JSON.stringify({ sessionId: sid, turnId, helpful }),
      });
      if (res.ok) {
        setMessages((prev) =>
          prev.map((m) => (m.turnId === turnId ? { ...m, feedbackSubmitted: true } : m)),
        );
      }
    } catch {
      /* ignore */
    }
  }, []);

  const pushInputHistory = useCallback((value: string) => {
    const text = value.trim();
    if (!text) return;

    const history = inputHistoryRef.current;
    if (history[history.length - 1] !== text) {
      inputHistoryRef.current = [...history, text].slice(-50);
    }
    inputHistoryIndexRef.current = null;
    inputHistoryDraftRef.current = "";
  }, []);

  const recallInputHistory = useCallback(
    (direction: -1 | 1) => {
      const history = inputHistoryRef.current;
      if (history.length === 0) return;

      const currentIndex = inputHistoryIndexRef.current;
      if (direction === -1) {
        const nextIndex = currentIndex === null ? history.length - 1 : Math.max(0, currentIndex - 1);
        if (currentIndex === null) inputHistoryDraftRef.current = input;
        inputHistoryIndexRef.current = nextIndex;
        setInput(history[nextIndex]);
        return;
      }

      if (currentIndex === null) return;
      if (currentIndex < history.length - 1) {
        const nextIndex = currentIndex + 1;
        inputHistoryIndexRef.current = nextIndex;
        setInput(history[nextIndex]);
        return;
      }

      inputHistoryIndexRef.current = null;
      setInput(inputHistoryDraftRef.current);
      inputHistoryDraftRef.current = "";
    },
    [input],
  );

  const send = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;

    pushInputHistory(text);
    setSending(true);
    setInput("");
    const assistantLocalId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `assistant-${Date.now()}`;

    let historyPayload: Array<{ role: string; content: string }> = [];
    setMessages((prev) => {
      historyPayload = prev.map((m) => ({ role: m.role, content: m.content }));
      return [
        ...prev,
        { role: "user" as const, content: text },
        { role: "assistant" as const, content: "", localId: assistantLocalId },
      ];
    });

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), FETCH_MS);

    try {
      const headers: Record<string, string> = {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      };

      const anonId = getOrCreateAnonId();
      const sid = sessionIdRef.current;

      const locale = typeof navigator !== "undefined" ? navigator.language : undefined;

      let donePayload: SupportChatResponse | null = null;

      const res = await fetch(`${getApiBase()}/web/ai/support-chat/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message: text,
          sessionId: sid ?? undefined,
          clientAnonymousId: anonId || undefined,
          locale: locale || undefined,
          history: historyPayload.filter((h) => h.role === "user" || h.role === "assistant"),
        }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error("Streaming request failed.");

      await readSseStream(res, (eventName, payload) => {
        if (eventName === "delta") {
          const textDelta =
            typeof payload === "object" && payload !== null && "text" in payload
              ? String((payload as { text?: unknown }).text ?? "")
              : "";
          if (!textDelta) return;
          setMessages((prev) =>
            prev.map((m) =>
              m.localId === assistantLocalId ? { ...m, content: `${m.content}${textDelta}` } : m,
            ),
          );
          return;
        }

        if (eventName === "done" && typeof payload === "object" && payload !== null) {
          donePayload = payload as SupportChatResponse;
        }
      });

      const data: SupportChatResponse = donePayload ?? {};

      if (data.sessionId) {
        setSessionId(data.sessionId);
        sessionIdRef.current = data.sessionId;
        try {
          window.localStorage.setItem(STORAGE_SESSION, data.sessionId);
        } catch {
          /* ignore */
        }
      }

      if (typeof data.handoffUrl === "string" && data.handoffUrl.trim().length > 0) {
        setHandoffUrl(data.handoffUrl.trim());
      }

      if (data.knowledgeBaseUpdatedUtc) {
        try {
          setKnowledgeUpdatedLabel(new Date(data.knowledgeBaseUpdatedUtc).toLocaleDateString());
        } catch {
          setKnowledgeUpdatedLabel(null);
        }
      }

      const raw =
        data.message?.trim() ||
        (data.rateLimited
          ? "Too many messages — please wait a moment."
          : "Sorry, something went wrong.");

      setMessages((prev) =>
        prev.map((m) => {
          if (m.localId !== assistantLocalId) return m;
          const finalText = m.content.trim().length > 0 ? m.content : raw;
          return {
            ...m,
            content: withDisclaimer(finalText),
            turnId: data.lastTurnId,
            sourceTitles: data.sourceTitles,
            lowConfidence: data.lowConfidence,
            guardrailBlocked: data.guardrailBlocked,
          };
        }),
      );
    } catch (e) {
      const aborted = e instanceof DOMException && e.name === "AbortError";
      const raw = aborted
        ? "That took too long — the server may be busy. Please try again in a moment."
        : "Could not reach the assistant. Check your connection or try again later.";
      setMessages((prev) =>
        prev.map((m) => (m.localId === assistantLocalId ? { ...m, content: withDisclaimer(raw) } : m)),
      );
    } finally {
      window.clearTimeout(timeoutId);
      setSending(false);
    }
  }, [input, pushInputHistory, sending]);

  if (!open) return null;

  return (
    <div data-bb-fah className="pointer-events-none absolute inset-0 z-40">
      <button
        type="button"
        className="pointer-events-auto fixed inset-0 z-40 bg-overlay lg:hidden"
        aria-label="Close BB FAH Assistant"
        onClick={() => setPanelOpen(false)}
      />
      <div
        className="pointer-events-auto absolute inset-y-0 right-0 z-40 flex w-[min(100%,22rem)] flex-col overflow-hidden border-l"
        style={{
          borderColor: "var(--xmegami-primary)",
          background: "linear-gradient(180deg, var(--xmegami-primary) 0%, var(--xmegami-primary-hover) 100%)",
          boxShadow: "0 25px 50px -12px rgba(190, 24, 93, 0.35)",
        }}
        id="bb-fah-assistant-panel"
        role="dialog"
        aria-label="BB FAH Assistant chat"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/20 px-4 py-3 text-white">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <img
              src={MASCOT_SRC}
              alt=""
              width={30}
              height={38}
              className="h-[38px] w-[30px] shrink-0 object-contain"
              draggable={false}
            />
            <span className="truncate text-base font-bold">BB FAH Assistant</span>
          </div>
          <button
            type="button"
            data-table-action
            className="rounded-lg p-2 text-white/95 hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            onClick={() => setPanelOpen(false)}
            aria-label="Close chat"
          >
            <X className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          </button>
        </div>
            <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto px-4 py-3 text-sm">
              {sessionId && messages.length === 1 ? (
                <div className="rounded-2xl border border-white/25 bg-white/10 px-3.5 py-2.5 text-xs text-white/90">
                  Continuing your saved conversation — ask a follow-up or start a new topic.
                </div>
              ) : null}
              {knowledgeUpdatedLabel ? (
                <p className="text-center text-[10px] text-white/60">
                  Knowledge materials last indexed: {knowledgeUpdatedLabel}
                </p>
              ) : null}
              {messages.map((m, i) => {
                const isLastAssistant =
                  m.role === "assistant" &&
                  i === messages.length - 1 &&
                  !sending &&
                  Boolean(m.turnId) &&
                  !m.feedbackSubmitted;
                return (
                  <div
                    key={m.turnId ? `${m.turnId}-${i}` : `${i}-${m.role}-${m.content.slice(0, 12)}`}
                    className={
                      m.role === "user"
                        ? "max-w-[92%] self-end rounded-2xl bg-white px-3.5 py-2.5 text-zinc-900"
                        : "max-w-[92%] self-start rounded-2xl bg-white/20 px-3.5 py-2.5 text-white"
                    }
                  >
                    {m.role === "assistant" && m.guardrailBlocked ? (
                      <p className="mb-2 rounded-md border border-white/25 bg-white/15 px-2 py-1 text-[11px] font-medium text-white/95">
                        Safety notice: general guidance only — not professional advice.
                      </p>
                    ) : null}
                    <div className="whitespace-pre-wrap break-words">
                      {renderChatRichText(m.content, `m-${i}-${m.role}`, m.role === "assistant")}
                    </div>
                    {isLastAssistant && m.turnId ? (
                      <div className="mt-2 flex items-center gap-2 border-t border-white/20 pt-2">
                        <span className="text-[10px] text-white/70">Helpful?</span>
                        <button
                          data-table-action
                          type="button"
                          className="rounded-lg border border-white/30 p-1 text-white hover:bg-white/15"
                          aria-label="Helpful"
                          onClick={() => void submitFeedback(m.turnId!, true)}
                        >
                          <ThumbsUp className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                        </button>
                        <button
                          data-table-action
                          type="button"
                          className="rounded-lg border border-white/30 p-1 text-white hover:bg-white/15"
                          aria-label="Not helpful"
                          onClick={() => void submitFeedback(m.turnId!, false)}
                        >
                          <ThumbsDown className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {sending &&
              (messages[messages.length - 1]?.role !== "assistant" ||
                messages[messages.length - 1]?.content.trim().length === 0) ? (
                <div className="max-w-[92%] self-start rounded-2xl bg-white/20 px-4 py-3 text-xs text-white/80">
                  Thinking…
                </div>
              ) : null}
            </div>
            {messages.length === 1 && messages[0]?.role === "assistant" ? (
              <div className="flex shrink-0 flex-wrap gap-2 border-t border-white/20 px-4 pb-2 pt-2">
                {SUGGESTED_PROMPTS.map((p) => (
                  <button
                    data-table-action
                    key={p}
                    type="button"
                    className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20"
                    onClick={() => void send(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="flex shrink-0 flex-col gap-2 border-t border-white/20 px-4 py-3">
              {handoffUrl ? (
                <a
                  href={handoffUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-1 text-xs font-medium text-white/95 underline-offset-2 hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
                  Contact human support
                </a>
              ) : null}
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => {
                    inputHistoryIndexRef.current = null;
                    inputHistoryDraftRef.current = "";
                    setInput(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      recallInputHistory(-1);
                      return;
                    }
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      recallInputHistory(1);
                      return;
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder="Ask BB FAH…"
                  className="min-w-0 flex-1 rounded-2xl border border-white/25 bg-white/15 px-4 py-3 text-sm text-white placeholder:text-white/60 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20"
                  disabled={sending}
                />
                <button
                  data-table-action
                  type="button"
                  onClick={() => void send()}
                  disabled={sending || !input.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[color:var(--xmegami-primary)] shadow-md transition hover:opacity-95 disabled:opacity-50"
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                </button>
              </div>
            </div>
      </div>
    </div>
  );
}
